import { CircularProgress, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { KeyboardEvent, useContext } from 'react';
import { Field, Form } from 'react-final-form';
import { Venture } from '../../api/ventures/types/Venture';
import { VenturesAdditionalInfo } from '../../api/ventures/types/VenturesAdditionalInfo';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { useScrollOnValidation } from '../../hooks/useScrollOnValidation';
import {
  DEDICATION,
  DONE_ALREADY,
  GROWTH_RATIO,
  lengthField,
  MARKET_SIZE,
  RADIO_SELECT,
  REASON_TO_START_NOW,
  SUPPORT_INTERESTED_RECEIVING,
} from '../../utils/form';
import { checkSizeTablet } from '../../utils/functions';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { length } from '../../utils/wysiwyg';
import {
  BeforeUnload,
  Button,
  CheckboxListOther,
  FormGroup,
  StickyContent,
} from '../common';
import {
  FormCheckbox,
  FormRadioboxGroup,
  FormSelect,
  TextFieldWysiwyg,
  TextFieldWrapper,
} from './wrappers';

interface Props {
  initialValues: object;
  isLoading: boolean;
  onSubmit: (value: FormAdditionalInfo) => void;
  hasAccessToUpdate?: boolean;
}

export type FormAdditionalInfo = Pick<
  VenturesAdditionalInfo,
  | 'howDidYouFind'
  | 'isLegallyRegistered'
  | 'businessDescription'
  | 'currentProgress'
  | 'requiredSupport'
  | 'marketSize'
  | 'reasonToStartNow'
  | 'plans'
  | 'growthRatio'
  | 'dedication'
  | 'yearPlans'
  | 'obstacles'
  | 'mentorshipPlans'
  | 'comments'
> &
  Pick<Venture, 'numOfEmployees' | 'isTech'>;
type Errors = {
  [K in keyof FormAdditionalInfo]?: string;
};

const useStyles = makeStyles((theme) => ({
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  sectionTitle: {
    marginBottom: 32,
  },
  formBlocks: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  logoFormBlock: {
    margin: '65px 0 0 80px',
  },
  mainFormBlock: {
    width: '100%',

    [theme.breakpoints.up(900)]: {
      width: 560,
    },
  },
  actionsBlock: {
    marginTop: 56,
  },
  checkbox: {
    paddingLeft: 0,
  },
  checkboxLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
    },
  },
}));

const OTHER = ',OTHER:';

const createValidateForm = (goalIntegrationEnabled: boolean) => (
  values: FormAdditionalInfo,
) => {
  const errors: Errors = {};
  const legallyValue = values.isLegallyRegistered as unknown;

  if (
    legallyValue === undefined ||
    legallyValue === null ||
    legallyValue === ''
  ) {
    errors.isLegallyRegistered = 'Required';
  }
  if (
    !values.businessDescription ||
    length(values.businessDescription || '') === 0
  ) {
    errors.businessDescription = 'Required';
  }
  if (!values.numOfEmployees) {
    errors.numOfEmployees = 'Required';
  }
  if (!values.currentProgress) {
    errors.currentProgress = 'Required';
  } else if (values.currentProgress.includes(OTHER)) {
    const parts = values.currentProgress.split(OTHER);
    if (parts.length < 2 || !parts[1] || !parts[1].trim()) {
      errors.currentProgress = 'Required';
    }
  }
  if (!values.requiredSupport) {
    errors.requiredSupport = 'Required';
  } else if (values.requiredSupport.includes(OTHER)) {
    const parts = values.requiredSupport.split(OTHER);
    if (parts.length < 2 || !parts[1] || !parts[1].trim()) {
      errors.requiredSupport = 'Required';
    }
  }
  if (!values.marketSize) {
    errors.marketSize = 'Required';
  }
  if (!values.reasonToStartNow || length(values.reasonToStartNow || '') === 0) {
    errors.reasonToStartNow = 'Required';
  } else if ((values.reasonToStartNow || '').length > lengthField.wysiwygMaxLength) {
    errors.reasonToStartNow = `Max ${lengthField.wysiwygMaxLength} characters (HTML)`;
  } else if (length(values.reasonToStartNow || '') > lengthField.wysiwygDisplayLimit) {
    errors.reasonToStartNow = `Max ${lengthField.wysiwygDisplayLimit} characters`;
  }
  if (!values.plans) {
    errors.plans = 'Required';
  }
  if (!values.growthRatio) {
    errors.growthRatio = 'Required';
  }
  if (!values.dedication) {
    errors.dedication = 'Required';
  }
  // Validate yearPlans and obstacles only when Goals Integration is disabled
  if (!goalIntegrationEnabled) {
    if (!values.yearPlans || length(values.yearPlans || '') === 0) {
      errors.yearPlans = 'Required';
    } else if ((values.yearPlans || '').length > lengthField.wysiwygMaxLength) {
      errors.yearPlans = `Max ${lengthField.wysiwygMaxLength} characters (HTML)`;
    } else if (length(values.yearPlans || '') > lengthField.wysiwygDisplayLimit) {
      errors.yearPlans = `Max ${lengthField.wysiwygDisplayLimit} characters`;
    }
    if (!values.obstacles || length(values.obstacles || '') === 0) {
      errors.obstacles = 'Required';
    } else if ((values.obstacles || '').length > lengthField.wysiwygMaxLength) {
      errors.obstacles = `Max ${lengthField.wysiwygMaxLength} characters (HTML)`;
    } else if (length(values.obstacles || '') > lengthField.wysiwygDisplayLimit) {
      errors.obstacles = `Max ${lengthField.wysiwygDisplayLimit} characters`;
    }
  }
  if (!values.mentorshipPlans || length(values.mentorshipPlans || '') === 0) {
    errors.mentorshipPlans = 'Required';
  } else if ((values.mentorshipPlans || '').length > lengthField.wysiwygMaxLength) {
    errors.mentorshipPlans = `Max ${lengthField.wysiwygMaxLength} characters (HTML)`;
  } else if (length(values.mentorshipPlans || '') > lengthField.wysiwygDisplayLimit) {
    errors.mentorshipPlans = `Max ${lengthField.wysiwygDisplayLimit} characters`;
  }

  // Optional fields validation
  if (values.howDidYouFind && values.howDidYouFind.length > lengthField.wysiwygMaxLength) {
    errors.howDidYouFind = `Max ${lengthField.wysiwygMaxLength} characters (HTML)`;
  } else if (values.howDidYouFind && length(values.howDidYouFind) > lengthField.wysiwygDisplayLimit) {
    errors.howDidYouFind = `Max ${lengthField.wysiwygDisplayLimit} characters`;
  }
  if (values.comments && (values.comments || '').length > lengthField.wysiwygMaxLength) {
    errors.comments = `Max ${lengthField.wysiwygMaxLength} characters (HTML)`;
  } else if (values.comments && length(values.comments || '') > lengthField.wysiwygDisplayLimit) {
    errors.comments = `Max ${lengthField.wysiwygDisplayLimit} characters`;
  }
  if (values.businessDescription && values.businessDescription.length > lengthField.wysiwygMaxLength) {
    errors.businessDescription = `Max ${lengthField.wysiwygMaxLength} characters (HTML)`;
  } else if (values.businessDescription && length(values.businessDescription) > lengthField.wysiwygDisplayLimit) {
    errors.businessDescription = `Max ${lengthField.wysiwygDisplayLimit} characters`;
  }
  return errors;
};

function VentureAdditionalInfoForm({
  initialValues,
  isLoading,
  onSubmit,
  hasAccessToUpdate = false,
}: Props) {
  const loading = isLoading;
  const classes = useStyles();
  const { rb } = useResourceBundles();
  const { user } = useContext(UserContext);
  const setSubmitValidationFailed = useScrollOnValidation();

  const goalIntegrationEnabled = user?.goalIntegrationEnabled ?? false;

  const handleSubmit = (value: FormAdditionalInfo) => {
    const normalizedIsLegallyRegistered =
      typeof value.isLegallyRegistered === 'string'
        ? value.isLegallyRegistered === 'true'
        : value.isLegallyRegistered;

    onSubmit({
      ...value,
      isLegallyRegistered: normalizedIsLegallyRegistered,
    });
  };

  const handleChangeField = (onChange: any, name: string, value: string) => {
    onChange(name, value);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (['-', '.', ',', 'e', 'E', '+'].includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <div>
      <Form
        validate={createValidateForm(goalIntegrationEnabled)}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        keepDirtyOnReinitialize
        render={(formProps) => {
          setSubmitValidationFailed(
            formProps.submitFailed &&
            !formProps.dirtySinceLastSubmit &&
            !formProps.submitting,
          );
          return (
            <div>
              <BeforeUnload
                when={formProps.dirty && !loading}
                title='Leave the page'
                body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
                disabled={loading}
                confirmButtonRenderer={({ onConfirm }) => (
                  <Button
                    variant='outlined'
                    onClick={async () => {
                      await formProps.handleSubmit();
                      onConfirm();
                    }}
                    disabled={loading || !formProps.valid}>
                    {loading ? (
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      'Save the changes'
                    )}
                  </Button>
                )}
              />
              <div className={classes.mainFormBlock}>
                <form noValidate>
                  <div className={classes.sectionBlock}>
                    <FormGroup>
                      <Field<string>
                        name='howDidYouFind'
                        testid='venture-additional-find'
                        component={TextFieldWysiwyg}
                        label='How did you learn about our program?'
                        placeholder='Type your answer here...'
                        readOnly={!hasAccessToUpdate}
                        showCount
                        maxLength={lengthField.wysiwygDisplayLimit}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string>
                        name='isLegallyRegistered'
                        testid='venture-additional-legally-registered'
                        component={FormRadioboxGroup}
                        options={RADIO_SELECT}
                        label='Are you legally registered business?*'
                        format={(val) =>
                          val === undefined || val === null ? val : String(val)
                        }
                        parse={(val) =>
                          val === undefined || val === null
                            ? val
                            : val === 'true'
                        }
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string>
                        name='businessDescription'
                        testid='venture-additional-business-description'
                        component={TextFieldWysiwyg}
                        label='Please describe what your business does*'
                        placeholder='Type your answer here...'
                        showCount
                        maxLength={lengthField.wysiwygDisplayLimit}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    <FormGroup mobile={checkSizeTablet(450)}>
                      <Field<any>
                        testid='venture-additional-number-employees'
                        name='numOfEmployees'
                        component={TextFieldWrapper}
                        parse={(val: string) => (val ? Number(val) : null)}
                        label='Number Of Employees*'
                        type='number'
                        InputProps={{
                          inputProps: {
                            readOnly: !hasAccessToUpdate,
                            min: 0,
                            onKeyPress: handleKeyPress,
                          },
                        }}
                      />
                      <Field<string>
                        testid='venture-additional-checkbox'
                        name='isTech'
                        component={FormCheckbox}
                        label='Is Tech'
                        readOnly={!hasAccessToUpdate}
                        className={classes.checkbox}
                        classNameLabel={classes.checkboxLabel}
                      />
                    </FormGroup>

                    <CheckboxListOther
                      name='currentProgress'
                      testid='venture-additional-current-progress'
                      options={DONE_ALREADY}
                      onChange={(value) =>
                        handleChangeField(
                          formProps.form.change,
                          'currentProgress',
                          value,
                        )
                      }
                      text='What have you done already? (select all that apply)*'
                      labelOther='Other option what have you already done'
                      value={formProps.values.currentProgress ?? undefined}
                      readOnly={!hasAccessToUpdate}
                    />

                    <CheckboxListOther
                      name='requiredSupport'
                      testid='venture-additional-required-support'
                      options={SUPPORT_INTERESTED_RECEIVING}
                      onChange={(value) =>
                        handleChangeField(
                          formProps.form.change,
                          'requiredSupport',
                          value,
                        )
                      }
                      text='What support are you seeking?*'
                      labelOther='Other'
                      value={formProps.values.requiredSupport ?? undefined}
                      readOnly={!hasAccessToUpdate}
                    />
                    <FormGroup>
                      <Field<string>
                        name='marketSize'
                        testid='venture-additional-market-size'
                        component={FormSelect}
                        label='How big is the opportunity you see in the market for your business?*'
                        options={MARKET_SIZE}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string>
                        name='reasonToStartNow'
                        testid='venture-additional-reason-start-now'
                        component={TextFieldWysiwyg}
                        label='Why now is the time to pursue this opportunity?*'
                        placeholder='Type your answer here...'
                        showCount
                        maxLength={lengthField.wysiwygDisplayLimit}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string>
                        name='plans'
                        testid='venture-additional-plans'
                        component={FormSelect}
                        label='What better describes your plans for this business?*'
                        options={REASON_TO_START_NOW}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string>
                        name='growthRatio'
                        testid='venture-additional-growth-ratio'
                        component={FormSelect}
                        label='How quickly do you see your business growing?*'
                        options={GROWTH_RATIO}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string>
                        name='dedication'
                        testid='venture-additional-dedication'
                        component={FormSelect}
                        label='Are you working on this business full-time?*'
                        options={DEDICATION}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    {!goalIntegrationEnabled && (
                      <>
                        <FormGroup>
                          <Field<string>
                            name='yearPlans'
                            testid='venture-additional-year-plans'
                            component={TextFieldWysiwyg}
                            label='What are you hoping to accomplish in the next 12 months?*'
                            placeholder='Type your answer here...'
                            showCount
                            maxLength={lengthField.wysiwygDisplayLimit}
                            readOnly={!hasAccessToUpdate}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Field<string>
                            name='obstacles'
                            testid='venture-additional-obstacles'
                            component={TextFieldWysiwyg}
                            label='What obstacles do you foresee in reaching these goals?*'
                            placeholder='Type your answer here...'
                            showCount
                            maxLength={lengthField.wysiwygDisplayLimit}
                            readOnly={!hasAccessToUpdate}
                          />
                        </FormGroup>
                      </>
                    )}

                    <FormGroup>
                      <Field<string>
                        name='mentorshipPlans'
                        testid='venture-additional-mentorship-plans'
                        component={TextFieldWysiwyg}
                        label={`How do you think ${rb(
                          'mentorship',
                        )} is going to help you achieve these?*`}
                        placeholder='Type your answer here...'
                        showCount
                        maxLength={lengthField.wysiwygDisplayLimit}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string>
                        name='comments'
                        testid='venture-additional-comments'
                        component={TextFieldWysiwyg}
                        label='Comments'
                        placeholder='Type here...'
                        showCount
                        maxLength={lengthField.wysiwygDisplayLimit}
                        readOnly={!hasAccessToUpdate}
                      />
                    </FormGroup>
                  </div>
                  {hasAccessToUpdate && (
                    <div className={classes.actionsBlock}>
                      <StickyContent>
                        <Button
                          className={CLASS_TRACKING.INTERNAL_ACTION}
                          onClick={formProps.handleSubmit}
                          disabled={loading}
                          startIcon={<CheckIcon />}>
                          {loading ? (
                            <CircularProgress size={24} color='inherit' />
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </StickyContent>
                    </div>
                  )}
                </form>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

export default VentureAdditionalInfoForm;
